import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "@/components/admin/admin-shell";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import {
  SCHOOL_MODULES, fetchModuleState, setModuleEnabled, defaultModuleState,
  type ModuleGroup, type ModuleKey, type ModuleState,
} from "@/lib/school-modules";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Loader2 } from "lucide-react";

export const Route = createFileRoute("/admin/modules")({
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({
    meta: [
      { title: "School modules · Makai Academy" },
      { name: "description", content: "Turn Makai school platform modules on or off — attendance, fees, timetables, transport and more — for your school." },
      { property: "og:title", content: "School modules · Makai Academy" },
      { property: "og:description", content: "Choose exactly which Makai modules your school runs." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ModulesPage,
});

const GROUPS: ModuleGroup[] = ["Teaching & learning", "Operations", "People", "Communication"];

function ModulesPage() {
  const { activeId, active } = useSchoolContext();
  const [state, setState] = useState<ModuleState>(() => defaultModuleState());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<ModuleKey | null>(null);

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    fetchModuleState(activeId).then((s) => { setState(s); setLoading(false); });
  }, [activeId]);

  const activeCount = useMemo(
    () => SCHOOL_MODULES.filter((m) => state[m.key]).length,
    [state],
  );

  async function toggle(key: ModuleKey, next: boolean) {
    if (!activeId) return;
    const previous = state[key];
    setState((s) => ({ ...s, [key]: next }));
    setSaving(key);
    try {
      await setModuleEnabled(activeId, key, next);
      toast.success(`${SCHOOL_MODULES.find((m) => m.key === key)?.label} ${next ? "switched on" : "switched off"}`);
    } catch (e) {
      setState((s) => ({ ...s, [key]: previous }));
      toast.error(e instanceof Error ? e.message : "Could not save that change");
    } finally {
      setSaving(null);
    }
  }

  return (
    <AdminShell
      title="School modules"
      subtitle={`${active?.school.name ?? "Your school"} · ${activeCount} of ${SCHOOL_MODULES.length} modules on`}
    >
      <p className="max-w-2xl text-sm text-muted-foreground">
        Run only the parts of Makai your school needs. Switching a module off hides it
        from staff, learners and parents straight away — the records stay safely stored
        and come back the moment you switch it on again.
      </p>

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your modules…
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          {GROUPS.map((group) => (
            <section key={group}>
              <h2 className="font-display text-lg font-semibold">{group}</h2>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {SCHOOL_MODULES.filter((m) => m.group === group).map((m) => (
                  <div
                    key={m.key}
                    className="flex items-start justify-between gap-4 rounded-2xl border border-border bg-card p-4"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{m.label}</span>
                        {m.sensitive && (
                          <Badge variant="secondary" className="gap-1 text-[10px]">
                            <ShieldCheck className="h-3 w-3" /> Restricted access
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{m.description}</p>
                    </div>
                    <Switch
                      checked={!!state[m.key]}
                      disabled={saving === m.key}
                      onCheckedChange={(v) => toggle(m.key, v)}
                      aria-label={`${m.label} module`}
                    />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
